import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import AgentDomain from "entities/domain/agents/agent-domain";
import { UnreadCountDTO } from "entities/dto/UnreadCountDTO";
import AgentsService from "services/agents";
import InboxService from "services/inbox";
import { keepNewerSocketCounts } from "./unread-counts-socket-writes";

// Query keys
export const currentAgentQueryKey = () => ["current-agent"];

export const unreadCountsQueryKey = () => ["unread-counts"];

/**
 * Hook to fetch the current logged-in agent
 */
export function useCurrentAgent(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { isAuthenticated, isLoading } = auth0Context;

  return useQuery<AgentDomain, Error>({
    queryKey: currentAgentQueryKey(),
    enabled: !isLoading && isAuthenticated,
    queryFn: async () => {
      return AgentsService.getCurrentAgent(auth0Context);
    },
    staleTime: staleTimeMs,
  });
}

/**
 * Hook to fetch unread counts for all merchants the agent belongs to. A
 * socket update written while the request was in flight is newer than its
 * answer and is kept (unread-counts-socket-writes).
 */
export function useUnreadCounts(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();

  return useQuery<UnreadCountDTO[], Error>({
    queryKey: unreadCountsQueryKey(),
    queryFn: async () => {
      try {
        const requestedAt = Date.now();
        return keepNewerSocketCounts(
          await InboxService.getUnreadCounts(auth0Context),
          requestedAt
        );
      } catch {
        return [];
      }
    },
    staleTime: staleTimeMs,
  });
}
